<template>
  <v-container grid-list-xl fluid>
    <confirm ref="confirm"></confirm>
    <v-snackbar :timeout="3000" bottom right :color="snackbar.color" v-model="snackbar.show">
      {{ snackbar.text }}
      <v-btn dark flat @click.native="snackbar.show = false" icon>
        <v-icon>close</v-icon>
      </v-btn>
    </v-snackbar>

    <!-- Header Card -->
    <v-card>
      <v-card-title>
        <span class="title">Dashboard Convenio Marco</span>
        <v-spacer></v-spacer>
        <v-btn fab dark small color="primary" style="margin:0px" @click="cargarDashboard()">
          <v-icon dark>refresh</v-icon>
        </v-btn>
      </v-card-title>
    </v-card>

    <!-- Filtros Card -->
    <v-card class="mt-3">
      <v-card-title>
        <span class="subheading">Filtros</span>
      </v-card-title>
      <v-card-text>
        <v-layout row wrap>
          <!-- Rango de Fechas -->
          <v-flex xs12 sm3 md3>
            <v-menu
              ref="menuFechaInicio"
              v-model="menuFechaInicio"
              :close-on-content-click="false"
              :nudge-right="40"
              lazy
              transition="scale-transition"
              offset-y
              full-width
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="filtros.fechaInicio"
                  label="Fecha Inicio"
                  prepend-icon="event"
                  readonly
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="filtros.fechaInicio"
                @input="menuFechaInicio = false; onFechaChange()"
              ></v-date-picker>
            </v-menu>
          </v-flex>
          
          <v-flex xs12 sm3 md3>
            <v-menu
              ref="menuFechaFin"
              v-model="menuFechaFin"
              :close-on-content-click="false"
              :nudge-right="40"
              lazy
              transition="scale-transition"
              offset-y
              full-width
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="filtros.fechaFin"
                  label="Fecha Fin"
                  prepend-icon="event"
                  readonly
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="filtros.fechaFin"
                @input="menuFechaFin = false; onFechaChange()"
              ></v-date-picker>
            </v-menu>
          </v-flex>

          <!-- Región -->
          <v-flex xs12 sm6 md6>
            <v-select
              :items="regionesDisponibles"
              v-model="filtros.regionCodigo"
              label="Región"
              clearable
              @change="cargarDashboard()"
            ></v-select>
          </v-flex>
        </v-layout>

        <!-- Controles -->
        <v-layout row wrap class="mt-2">
          <v-flex xs12 sm6>
            <v-btn color="green" dark @click="exportarExcel()" :loading="loadingExport">
              <v-icon left>attach_file</v-icon>
              Exportar Excel
            </v-btn>
          </v-flex>
          <v-flex xs12 sm6 class="text-xs-right">
            <v-chip color="info" text-color="white" v-if="resumenGeneral.totalOCs">
              Período: {{ formatDateRange() }}
            </v-chip>
          </v-flex>
        </v-layout>

        <!-- Resumen General -->
        <v-layout row wrap class="mt-3" v-if="resumenGeneral.totalOCs">
          <v-flex xs12>
            <v-card color="blue lighten-5" elevation="0">
              <v-card-text>
                <v-layout row wrap>
                  <v-flex xs12 sm4 class="text-xs-center">
                    <div class="display-1 font-weight-bold blue--text">{{ resumenGeneral.totalOCs }}</div>
                    <div class="subheading">Órdenes de Compra</div>
                  </v-flex>
                  <v-flex xs12 sm4 class="text-xs-center">
                    <div class="display-1 font-weight-bold green--text">{{ formatPrice(resumenGeneral.totalVentas) }}</div>
                    <div class="subheading">Ventas Totales</div>
                  </v-flex>
                  <v-flex xs12 sm4 class="text-xs-center">
                    <div class="display-1 font-weight-bold orange--text">{{ formatNumber(resumenGeneral.totalUnidades) }}</div>
                    <div class="subheading">Unidades Vendidas</div>
                  </v-flex>
                </v-layout>
              </v-card-text>
            </v-card>
          </v-flex>
        </v-layout>
      </v-card-text>
    </v-card>

    <!-- Dashboard Gráficos -->
    <v-layout row wrap class="mt-3">
      <!-- 1. Ventas por Empresa (Donut) -->
      <v-flex xs12 md6 class="pa-2">
        <v-card style="height: 500px;">
          <v-card-title class="pb-2">
            <span class="subheading">
              <v-icon left color="primary">business</v-icon>
              Participación por Empresa
            </span>
            <v-spacer></v-spacer>
            <v-chip small color="info" text-color="white">
              {{ ventasPorEmpresa.labels ? ventasPorEmpresa.labels.length : 0 }} empresas
            </v-chip>
          </v-card-title>
          <v-card-text class="pt-0" style="height: 420px;">
            <donut-empresas
              :labels="ventasPorEmpresa.labels"
              :data="ventasPorEmpresa.data"
              :detalle="ventasPorEmpresa.detalle"
            />
          </v-card-text>
        </v-card>
      </v-flex>

      <!-- 2. Ventas por Región (Barras Horizontales) -->
      <v-flex xs12 md6 class="pa-2">
        <v-card style="height: 500px;">
          <v-card-title class="pb-2">
            <span class="subheading">
              <v-icon left color="primary">place</v-icon>
              Ventas por Región
            </span>
            <v-spacer></v-spacer>
            <v-btn-toggle v-model="vistaRegion" mandatory dense class="mr-2">
              <v-btn small value="ventas">$</v-btn>
              <v-btn small value="unidades">Unid.</v-btn>
            </v-btn-toggle>
            <v-chip small color="success" text-color="white">
              {{ ventasPorRegion.labels ? ventasPorRegion.labels.length : 0 }} regiones
            </v-chip>
          </v-card-title>
          <v-card-text class="pt-0" style="height: 420px;">
            <barras-region
              :labels="ventasPorRegion.labels"
              :data="ventasPorRegion.data"
              :detalle="ventasPorRegion.detalle"
              :vista="vistaRegion"
            />
          </v-card-text>
        </v-card>
      </v-flex>

      <!-- 3. Ventas por Producto (Barras Verticales) -->
      <v-flex xs12 class="pa-2">
        <v-card style="height: 600px;">
          <v-card-title class="pb-2">
            <span class="subheading">
              <v-icon left color="primary">whatshot</v-icon>
              Ventas por Producto (Top 15)
            </span>
            <v-spacer></v-spacer>
            <v-btn-toggle v-model="vistaProductos" mandatory class="mr-2">
              <v-btn small value="ventas">Ventas</v-btn>
              <v-btn small value="unidades">Unidades</v-btn>
            </v-btn-toggle>
            <v-chip small color="warning" text-color="white">
              {{ ventasPorProducto.labels ? ventasPorProducto.labels.length : 0 }} productos
            </v-chip>
          </v-card-title>
          <v-card-text class="pt-0" style="height: 520px;">
            <barras-productos
              :labels="ventasPorProducto.labels"
              :data="ventasPorProducto.data"
              :detalle="ventasPorProducto.detalle"
              :vista="vistaProductos"
              :top="15"
            />
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>

    <!-- Tabla de Detalle -->
    <v-card class="mt-3" v-if="mostrarTablaDetalle && datosTablaDetalle.length > 0">
      <v-card-title>
        <span class="subheading">Detalle de Productos</span>
        <v-spacer></v-spacer>
        <v-btn icon @click="mostrarTablaDetalle = false">
          <v-icon>close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text>
        <v-data-table
          :headers="headersTablaDetalle"
          :items="datosTablaDetalle"
          :search="busquedaTabla"
          class="elevation-1"
          :rows-per-page-items="[10, 25, 50]"
          :pagination.sync="paginationTabla"
        >
          <template slot="items" slot-scope="props">
            <td class="text-xs-left">{{ props.item.codigoProducto }}</td>
            <td class="text-xs-left truncate-text">{{ props.item.nombreProducto }}</td>
            <td class="text-xs-center">{{ props.item.regionMuestra }}</td>
            <td class="text-xs-left">{{ props.item.nombreEmpresa }}</td>
            <td class="text-xs-right">{{ formatPrice(props.item.totalVentas) }}</td>
            <td class="text-xs-right">{{ formatNumber(props.item.totalUnidades) }}</td>
            <td class="text-xs-right">{{ formatPrice(props.item.precioPromedio) }}</td>
            <td class="text-xs-right">{{ props.item.cantidadLineasOC }}</td>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Botón para mostrar tabla -->
    <v-layout row wrap class="mt-3" v-if="ventasPorProducto.detalle && ventasPorProducto.detalle.length > 0">
      <v-flex xs12 class="text-xs-center">
        <v-btn 
          color="info" 
          flat 
          @click="toggleTablaDetalle()"
          :loading="loadingTabla"
        >
          <v-icon left>{{ mostrarTablaDetalle ? 'keyboard_arrow_up' : 'keyboard_arrow_down' }}</v-icon>
          {{ mostrarTablaDetalle ? 'Ocultar' : 'Ver' }} Detalle de Productos
        </v-btn>
      </v-flex>
    </v-layout>

    <loader :value="loading" message="Cargando dashboard" progressColor="blue"></loader>
  </v-container>
</template>

<script>
import DashboardCMService from "@/services/DashboardCMService";
import DateRangePicker from "@/components/DateRangePicker";
import DonutEmpresas from "@/components/DonutEmpresas";
import BarrasRegion from "@/components/BarrasRegion";
import BarrasProductos from "@/components/BarrasProductos";
import Loader from "@/components/Loader";
import moment from "moment";

export default {
  components: {
    DateRangePicker,
    DonutEmpresas,
    BarrasRegion,
    BarrasProductos,
    Loader
  },
  data: () => ({
    snackbar: {
      show: false,
      text: "",
      color: ""
    },
    loading: false,
    loadingExport: false,
    loadingTabla: false,
    menuFechaInicio: false,
    menuFechaFin: false,
    vistaProductos: 'ventas',
    vistaRegion: 'ventas',
    mostrarTablaDetalle: false,
    busquedaTabla: "",

    filtros: {
      fechaInicio: null,
      fechaFin: null,
      regionCodigo: null
    },

    resumenGeneral: {
      totalOCs: 0,
      totalVentas: 0,
      totalUnidades: 0
    },
    ventasPorEmpresa: {
      labels: [],
      data: [],
      detalle: []
    },
    ventasPorRegion: {
      labels: [],
      data: [],
      detalle: []
    },
    ventasPorProducto: {
      labels: [],
      data: [],
      detalle: []
    },
    regionesDisponibles: [],

    datosTablaDetalle: [],
    headersTablaDetalle: [
      {
        text: 'Código',
        align: 'left',
        sortable: true,
        value: 'codigoProducto',
        width: '120px'
      },
      {
        text: 'Producto',
        align: 'left',
        sortable: true,
        value: 'nombreProducto'
      },
      {
        text: 'Región',
        align: 'center',
        sortable: true,
        value: 'regionMuestra',
        width: '80px'
      },
      {
        text: 'Empresa',
        align: 'left',
        sortable: true,
        value: 'nombreEmpresa',
        width: '150px'
      },
      {
        text: 'Ventas',
        align: 'right',
        sortable: true,
        value: 'totalVentas',
        width: '120px'
      },
      {
        text: 'Unidades',
        align: 'right',
        sortable: true,
        value: 'totalUnidades',
        width: '100px'
      },
      {
        text: 'Precio Prom.',
        align: 'right',
        sortable: true,
        value: 'precioPromedio',
        width: '120px'
      },
      {
        text: 'OCs',
        align: 'right',
        sortable: true,
        value: 'cantidadLineasOC',
        width: '80px'
      }
    ],
    paginationTabla: {
      rowsPerPage: 25
    }
  }),

  methods: {
    onFechaChange() {
      // Validar que ambas fechas estén seleccionadas antes de cargar datos
      if (this.filtros.fechaInicio && this.filtros.fechaFin) {
        // Validar que fecha inicio no sea mayor que fecha fin
        if (this.filtros.fechaInicio <= this.filtros.fechaFin) {
          this.cargarDashboard();
        } else {
          this.snackbar = {
            show: true,
            color: 'warning',
            text: 'La fecha de inicio no puede ser mayor que la fecha fin'
          };
        }
      }
    },

    handleDateRangeChange({ start, end }) {
      this.filtros.fechaInicio = start;
      this.filtros.fechaFin = end;
      if (end !== null) {
        this.cargarDashboard();
      }
    },

    async cargarDashboard() {
      this.loading = true;
      try {
        const params = this.construirParametros();
        const response = await DashboardCMService.getDashboardConvenioMarco(params);
        
        this.resumenGeneral = response.data.resumenGeneral || {
          totalOCs: 0,
          totalVentas: 0,
          totalUnidades: 0
        };
        
        this.ventasPorEmpresa = response.data.ventasPorEmpresa || { 
          labels: [], 
          data: [], 
          detalle: [] 
        };
        
        this.ventasPorRegion = response.data.ventasPorRegion || { 
          labels: [], 
          data: [], 
          detalle: [] 
        };
        
        this.ventasPorProducto = response.data.ventasPorProducto || { 
          labels: [], 
          data: [], 
          detalle: [] 
        };

        // Actualizar datos de tabla si está visible
        if (this.mostrarTablaDetalle) {
          this.datosTablaDetalle = this.expandirDatosTabla();
        } else {
          // Si no está visible, resetear para forzar recálculo al mostrar
          this.datosTablaDetalle = [];
        }

        this.snackbar = {
          show: true,
          color: 'success',
          text: `Dashboard cargado: ${this.resumenGeneral.totalOCs} OCs`
        };

      } catch (error) {
        console.error('Error cargando dashboard:', error);
        this.snackbar = {
          show: true,
          color: 'error',
          text: 'Error al cargar el dashboard'
        };
      } finally {
        this.loading = false;
      }
    },

    async exportarExcel() {
      this.loadingExport = true;
      try {
        const params = this.construirParametros();
        await DashboardCMService.exportarDashboardExcel(params);
        
        this.snackbar = {
          show: true,
          color: 'success',
          text: 'Archivo Excel descargado exitosamente'
        };
      } catch (error) {
        console.error('Error exportando Excel:', error);
        this.snackbar = {
          show: true,
          color: 'error',
          text: 'Error al exportar el archivo Excel'
        };
      } finally {
        this.loadingExport = false;
      }
    },

    async cargarRegiones() {
      try {
        const response = await DashboardCMService.getRegionesDisponibles();
        this.regionesDisponibles = response.data.map(region => ({
          text: `${region.nombre} (${region.codigo})`,
          value: region.codigo
        }));
      } catch (error) {
        console.error('Error cargando regiones:', error);
      }
    },

    toggleTablaDetalle() {
      this.mostrarTablaDetalle = !this.mostrarTablaDetalle;
      
      if (this.mostrarTablaDetalle && this.datosTablaDetalle.length === 0) {
        this.datosTablaDetalle = this.expandirDatosTabla();
      }
    },

    expandirDatosTabla() {
      const filasExpandidas = [];
      
      if (!this.ventasPorProducto.detalle) return filasExpandidas;
      
      this.ventasPorProducto.detalle.forEach(producto => {
        if (producto.empresas && Array.isArray(producto.empresas)) {
          producto.empresas.forEach(empresa => {
            filasExpandidas.push({
              codigoProducto: producto.codigoProducto,
              nombreProducto: producto.nombreProducto,
              regionMuestra: producto.regionMuestra,
              nombreEmpresa: empresa.nombreEmpresa,
              totalVentas: empresa.totalVentas,
              totalUnidades: empresa.totalUnidades,
              precioPromedio: empresa.precioPromedio,
              cantidadLineasOC: empresa.cantidadLineasOC
            });
          });
        }
      });
      
      // Ordenar por ventas descendente
      return filasExpandidas.sort((a, b) => b.totalVentas - a.totalVentas);
    },

    construirParametros() {
      const params = {};
      if (this.filtros.fechaInicio) params.fechaInicio = this.filtros.fechaInicio;
      if (this.filtros.fechaFin) params.fechaFin = this.filtros.fechaFin;
      if (this.filtros.regionCodigo) params.regionCodigo = this.filtros.regionCodigo;
      return params;
    },

    formatPrice(value) {
      if (!value) return '$0';
      const numValue = parseFloat(value);
      
      if (numValue >= 1000000000) {
        return '$' + (numValue / 1000000000).toFixed(1) + 'B';
      } else if (numValue >= 1000000) {
        return '$' + (numValue / 1000000).toFixed(0) + 'M';
      } else if (numValue >= 1000) {
        return '$' + (numValue / 1000).toFixed(0) + 'K';
      } else {
        return '$' + Math.round(numValue).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
      }
    },

    formatNumber(value) {
      if (!value) return '0';
      const numValue = Math.round(parseFloat(value));
      return numValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    },

    formatDateRange() {
      if (this.filtros.fechaInicio && this.filtros.fechaFin) {
        return `${this.filtros.fechaInicio} al ${this.filtros.fechaFin}`;
      } else if (this.filtros.fechaInicio) {
        return `Desde ${this.filtros.fechaInicio}`;
      } else {
        return 'Todos los períodos';
      }
    }
  },

  async mounted() {
    await this.cargarRegiones();
    await this.cargarDashboard();
  }
};
</script>

<style scoped>
.v-progress-circular {
  display: block;
  width: 100px;
  margin: 0 auto;
}

.truncate-text {
  max-width: 300px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.v-card {
  transition: all 0.3s ease;
}

.v-btn-toggle .v-btn {
  min-width: 40px !important;
  padding: 0 8px !important;
}

@media (max-width: 960px) {
  .display-1 {
    font-size: 2rem !important;
  }
}
</style>